import { isMarketplaceTest } from 'modules/test/marketplace-policy';

/** UX gating only — the server remains the source of truth on Activate. */
export interface TestReadiness {
  qCount: number;
  /** Sections filled from the section library (random slot or pinned block); their questions are drawn per attempt. */
  librarySectionCount: number;
  subjectCount: number;
  pointsTotal: number;
  duration: number;
  passingScore: number | null;
  respondents: number;
  attempts: number | null;
  hasQuestions: boolean;
  hasTiming: boolean;
  hasAccess: boolean;
  doneCount: number;
  total: number;
  allReady: boolean;
  availableFrom?: string;
  availableUntil?: string;
  /** Public candidate-link token, when the test has been shared. */
  shareToken?: string;
  publicLinkSlug?: string;
}

// The mapped TestDetails is loosely typed (fields propagated via `as any`), so
// this reads defensively. Accepts `unknown` so callers can pass the hook's
// `item` straight through without a lossy double-cast.
export function computeTestReadiness(input: unknown): TestReadiness {
  const test = (input ?? undefined) as Record<string, any> | undefined;
  const questions: any[] = Array.isArray(test?.questions) ? test!.questions : [];
  const qCount = questions.length || test?.totalQuestions || 0;
  // Same rule as the API's exam readiness: these sections hold no questions of
  // their own, so they count as content without adding to qCount.
  const sections: any[] = Array.isArray(test?.sections) ? test!.sections : [];
  const librarySectionCount = sections.filter(
    (s) => s?.source?.mode === 'RANDOM' || Boolean(s?.source?.blockId),
  ).length;

  const subjectConfigs: any[] = Array.isArray(test?.subjectConfigs) ? test!.subjectConfigs : [];
  const subjectCount = subjectConfigs.length
    ? subjectConfigs.length
    : new Set(questions.map((q) => q?.subjectId).filter(Boolean)).size;
  const pointsTotal = subjectConfigs.length
    ? subjectConfigs.reduce((s, c) => s + (c?.questionCount || 0) * (c?.pointsPerQuestion || 1), 0)
    : questions.reduce((s, q) => s + (q?.points || 0), 0);

  const duration = test?.duration ?? 0;
  const marketplace = isMarketplaceTest(test);
  const passingScore = marketplace ? null : test?.passingScore ?? 0;
  const respondents = test?.stats?.totalAttempts ?? 0;
  const access = test?.config?.access;
  const shareToken: string | undefined = test?.publicLink?.token;

  const hasQuestions = qCount > 0 || librarySectionCount > 0;
  const hasTiming = duration > 0;
  const hasAccess = Boolean(shareToken) || Boolean(access?.accessType && access.accessType !== 'none');

  const doneCount = [hasQuestions, hasTiming, hasAccess].filter(Boolean).length;

  return {
    qCount,
    librarySectionCount,
    subjectCount,
    pointsTotal,
    duration,
    passingScore,
    respondents,
    attempts: marketplace ? null : access?.attemptsPerRespondent ?? 1,
    hasQuestions,
    hasTiming,
    hasAccess,
    doneCount,
    total: 3,
    allReady: doneCount === 3,
    availableFrom: test?.availableFrom,
    availableUntil: test?.availableUntil,
    shareToken,
    publicLinkSlug: test?.publicLinkSlug,
  };
}
